<template>
  <div :class="$style.avatarCircle">
    <img
      v-if="imageUrl"
      :src="`${imageUrl}/s/300/300`"
      :class="$style.avatarImage"
      :alt="firstName + ' ' + lastName"
      @error.once="onImageError"
    />
    <div v-if="!imageUrl" :class="$style.avatarImage"></div>
    <span v-if="!imageUrl" :class="$style.avatarInitials">
      {{ initials }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { datadogRum } from '@datadog/browser-rum';
import { computed, ref } from 'vue';

defineOptions({
  name: 'AgentAvatar',
});

interface Props {
  avatarUrl: string | null | undefined;
  firstName: string;
  lastName: string;
}

const props = withDefaults(defineProps<Props>(), {
  avatarUrl: null,
});

const imageUrl = ref<string | null>(props.avatarUrl);
console.log('imageUrl', imageUrl.value);

const initials = computed(() => {
  const lettersFirstName = props.firstName.split(' ').map((n) => n[0]);
  const lettersLastName = props.lastName.split(' ').map((n) => n[0]);

  // only return a max of 3 letters; prefer using initials of last name
  let initials: string[] = [];

  if (lettersFirstName.length >= 1) {
    initials.push(lettersFirstName[0]);
  }
  if (lettersFirstName.length >= 2 && lettersLastName.length <= 1) {
    initials.push(lettersFirstName[1]);
  }

  if (lettersLastName.length >= 1) {
    initials.push(lettersLastName[0]);
  }

  if (lettersLastName.length >= 2) {
    initials.push(lettersLastName[1]);
  }

  return initials.join('');
});

function onImageError() {
  imageUrl.value = null;

  datadogRum.addError(new Error('Failed to load agent image on PDP'), {
    file: { url: props.avatarUrl },
    tags: { 'custom-logging': 'RP-584' },
  });
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

.avatarCircle {
  position: relative;
  display: block;
  width: 68px;
  height: 68px;
  padding: 2px;
  border: 1px solid $color-grey-50;
  border-radius: 50%;
}

.avatarImage {
  width: 68px;
  height: 68px;
  object-fit: cover;
  background-color: $color-grey-50;
  border-radius: 50%;
}

.avatarInitials {
  position: absolute;
  top: 50%;
  left: 50%;
  width: fit-content;
  margin: 0;
  font-size: 24px;
  font-weight: normal;
  color: $color-grey-90;
  transform: translate(-50%, -50%);
}
</style>
